import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X, Check, ChevronLeft, ChevronRight, CreditCard } from "lucide-react";

const STYLE_COLORS = {};

export function RegistrationModal({ open, onOpenChange }) {
  const [step, setStep] = useState(1);
  const [formulas, setFormulas] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formula, setFormula] = useState(null);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [installments, setInstallments] = useState(1);
  const [info, setInfo] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    birthdate: "",
  });
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (open) {
      api
        .get("/formulas")
        .then(({ data }) => setFormulas(data))
        .catch(() => {});
      api
        .get("/courses")
        .then(({ data }) => {
          if (Array.isArray(data)) {
            setCourses(data);
          } else {
            console.error("Invalid /courses response:", data);
            setCourses([]);
          }
        })
        .catch((error) => {
          console.error("Unable to load courses:", error);
          setCourses([]);
        });
      setStep(1);
      setFormula(null);
      setSelectedCourses([]);
      setInstallments(1);
      setInfo({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        address: "",
        birthdate: "",
      });
      setConsent(false);
    }
  }, [open]);

  const selectedFormula = formulas.find((f) => f.key === formula);
  const maxCourses = selectedFormula?.courses ?? 0;

  const toggleCourse = (title) => {
    setSelectedCourses((prev) => {
      if (prev.includes(title)) return prev.filter((c) => c !== title);
      if (prev.length >= maxCourses) {
        toast.info(
          `Cette formule permet ${maxCourses >= 99 ? "un accès illimité" : maxCourses + " cours"}.`,
        );
        if (maxCourses >= 99) return [...prev, title];
        return prev;
      }
      return [...prev, title];
    });
  };

  const canNext =
    (step === 1 && !!formula) ||
    (step === 2 && selectedCourses.length > 0) ||
    (step === 3 && info.first_name && info.last_name && info.email && consent);

  const amount = selectedFormula?.amount || 0;
  const perInstallment = installments === 3 ? amount / 3 : amount;

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/registrations", {
        ...info,
        formula,
        courses: selectedCourses,
        installments,
        consent_rgpd: consent,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="registration-payment-modal"
        className="max-w-2xl bg-ink-2 border border-white/10 text-cream p-0 overflow-hidden max-h-[90vh] flex flex-col"
      >
        <DialogTitle className="sr-only">
          Inscription à l'année — AJDR Factory
        </DialogTitle>
        {/* header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <p className="font-mono2 text-[11px] uppercase tracking-widest text-emerald-bright">
              Saison 2026/2027
            </p>
            <h3 className="font-display font-bold text-xl mt-1">
              Inscription à l'année
            </h3>
          </div>
          <button
            data-testid="registration-close"
            onClick={() => onOpenChange(false)}
            className="text-cream/60 hover:text-cream"
          >
            <X />
          </button>
        </div>

        {/* steps indicator */}
        <div className="px-6 pt-4 flex items-center gap-2">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition ${s <= step ? "bg-mustard" : "bg-white/10"}`}
            />
          ))}
        </div>

        <div className="p-6 overflow-y-auto">
          {/* STEP 1 — formula */}
          {step === 1 && (
            <div data-testid="reg-step-formula">
              <h4 className="font-sub font-semibold text-lg mb-4">
                Choisis ta formule
              </h4>
              <div className="grid gap-3">
                {formulas.map((f) => (
                  <button
                    key={f.key}
                    data-testid={`formula-${f.key}`}
                    onClick={() => {
                      setFormula(f.key);
                      setSelectedCourses([]);
                    }}
                    className={`text-left rounded-2xl p-5 border transition ${formula === f.key ? "border-mustard bg-mustard/10" : "border-white/10 bg-ink hover:border-white/25"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-sub font-bold text-lg">
                        {f.name}
                      </span>
                      <span className="font-display font-black text-xl text-mustard">
                        {f.amount}€
                        <span className="text-xs text-cream/40 font-body font-normal">
                          /an
                        </span>
                      </span>
                    </div>
                    <p className="text-sm text-cream/60 mt-1">{f.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2 — courses */}
          {step === 2 && (
            <div data-testid="reg-step-courses">
              <h4 className="font-sub font-semibold text-lg mb-1">
                Positionne-toi sur tes cours
              </h4>
              <p className="text-sm text-cream/50 mb-4">
                {maxCourses >= 99
                  ? "Accès illimité — choisis autant de cours que tu veux."
                  : `Sélectionne jusqu'à ${maxCourses} cours.`}{" "}
                ({selectedCourses.length} sélectionné
                {selectedCourses.length > 1 ? "s" : ""})
              </p>
              <div className="grid sm:grid-cols-2 gap-2.5 max-h-[46vh] overflow-y-auto pr-1">
                {courses.map((c) => {
                  const sel = selectedCourses.includes(c.title);
                  return (
                    <button
                      key={c.id}
                      data-testid={`select-course-${c.slug}`}
                      onClick={() => toggleCourse(c.title)}
                      className={`text-left rounded-xl p-3.5 border transition flex items-start justify-between gap-2 ${sel ? "border-mustard bg-mustard/10" : "border-white/10 bg-ink hover:border-white/25"}`}
                    >
                      <div className="min-w-0">
                        <div className="font-sub font-semibold text-sm truncate">
                          {c.title}
                        </div>
                        <div className="font-mono2 text-[11px] text-cream/50 mt-0.5">
                          {c.day} · {c.start} · {c.teacher}
                        </div>
                      </div>
                      {sel && (
                        <span className="shrink-0 w-5 h-5 rounded-full bg-mustard flex items-center justify-center">
                          <Check className="w-3 h-3 text-ink" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 3 — infos */}
          {step === 3 && (
            <div data-testid="reg-step-info">
              <h4 className="font-sub font-semibold text-lg mb-4">
                Tes informations
              </h4>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field
                  label="Prénom *"
                  testid="reg-first-name"
                  value={info.first_name}
                  onChange={(v) => setInfo({ ...info, first_name: v })}
                />
                <Field
                  label="Nom *"
                  testid="reg-last-name"
                  value={info.last_name}
                  onChange={(v) => setInfo({ ...info, last_name: v })}
                />
                <Field
                  label="Email *"
                  type="email"
                  testid="reg-email"
                  value={info.email}
                  onChange={(v) => setInfo({ ...info, email: v })}
                />
                <Field
                  label="Téléphone"
                  testid="reg-phone"
                  value={info.phone}
                  onChange={(v) => setInfo({ ...info, phone: v })}
                />
                <Field
                  label="Date de naissance"
                  type="date"
                  testid="reg-birthdate"
                  value={info.birthdate}
                  onChange={(v) => setInfo({ ...info, birthdate: v })}
                />
                <Field
                  label="Adresse"
                  testid="reg-address"
                  value={info.address}
                  onChange={(v) => setInfo({ ...info, address: v })}
                />
              </div>
              <label className="mt-4 flex items-start gap-2 text-xs text-cream/60 cursor-pointer">
                <input
                  data-testid="reg-consent"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 accent-[#DD3733]"
                />
                <span>
                  J'accepte que mes données soient traitées pour la gestion de
                  mon inscription, conformément au RGPD. Elles ne seront jamais
                  cédées à des tiers.
                </span>
              </label>
            </div>
          )}

          {/* STEP 4 — payment */}
          {step === 4 && (
            <div data-testid="reg-step-payment">
              <h4 className="font-sub font-semibold text-lg mb-4">
                Récapitulatif & paiement
              </h4>
              <div className="rounded-2xl bg-ink border border-white/10 p-5 space-y-2 text-sm">
                <Row label="Formule" value={selectedFormula?.name} />
                <Row label="Cours" value={selectedCourses.join(", ")} />
                <Row
                  label="Adhérent"
                  value={`${info.first_name} ${info.last_name}`}
                />
                <div className="border-t border-white/10 my-2" />
                <Row
                  label="Total saison"
                  value={`${amount.toFixed(2)} €`}
                  strong
                />
              </div>

              <p className="mt-5 mb-2 font-sub font-semibold text-sm">
                Mode de paiement CB
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="pay-1x"
                  onClick={() => setInstallments(1)}
                  className={`rounded-xl p-4 border text-left transition ${installments === 1 ? "border-mustard bg-mustard/10" : "border-white/10 bg-ink"}`}
                >
                  <div className="font-sub font-bold">En 1 fois</div>
                  <div className="font-display font-black text-lg text-mustard mt-1">
                    {amount.toFixed(2)} €
                  </div>
                </button>
                <button
                  data-testid="pay-3x"
                  onClick={() => setInstallments(3)}
                  className={`rounded-xl p-4 border text-left transition ${installments === 3 ? "border-mustard bg-mustard/10" : "border-white/10 bg-ink"}`}
                >
                  <div className="font-sub font-bold">
                    En 3 fois{" "}
                    <span className="text-emerald-bright text-[10px] font-mono2">
                      sans frais
                    </span>
                  </div>
                  <div className="font-display font-black text-lg text-mustard mt-1">
                    3 × {(amount / 3).toFixed(2)} €
                  </div>
                </button>
              </div>
              <p className="mt-3 text-xs text-cream/45 font-mono2">
                {installments === 3
                  ? `Aujourd'hui : ${perInstallment.toFixed(2)} € (1er versement). Les 2 suivants seront prélevés mensuellement.`
                  : "Paiement unique aujourd'hui."}
              </p>
              <div className="mt-3 flex items-center gap-2 text-xs text-cream/40">
                <CreditCard className="w-4 h-4" /> Paiement sécurisé via Stripe
                · carte de test 4242 4242 4242 4242
              </div>
            </div>
          )}
        </div>

        {/* footer nav */}
        <div className="p-5 border-t border-white/10 flex items-center justify-between gap-3">
          <button
            data-testid="reg-back"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            className="inline-flex items-center gap-1 text-sm text-cream/60 hover:text-cream disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" /> Retour
          </button>

          {step < 4 ? (
            <button
              data-testid="reg-next"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canNext}
              className="btn-mustard rounded-full px-6 py-2.5 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continuer <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="reg-pay-btn"
              onClick={submit}
              disabled={loading}
              className="btn-mustard rounded-full px-7 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
            >
              <CreditCard className="w-4 h-4" />{" "}
              {loading
                ? "Redirection..."
                : `Payer ${perInstallment.toFixed(2)} €`}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs text-cream/50">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg bg-ink border border-white/10 px-3 py-2 text-sm text-cream focus:border-mustard focus:outline-none"
      />
    </label>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-cream/50">{label}</span>
      <span
        className={`text-right ${strong ? "font-display font-black text-mustard text-base" : "text-cream/90"}`}
      >
        {value || "—"}
      </span>
    </div>
  );
}
